import React, { useState, useEffect } from 'react';
import { Activity, TrendingUp, BarChart3, Users as UsersIcon, Calendar, CalendarCheckIcon, CalendarClock, RefreshCw, Smartphone, ChevronDown, ChevronRight, Megaphone } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { analyticsAPI } from '../../../config/analytics-api';
import { userAPI } from '../../../config/user-api';
import './Analytics.css';
import '../Users/Users.css';

const Analytics = () => {
  const [analyticsTimeFrame, setAnalyticsTimeFrame] = useState('30mins');
  const [analyticsData, setAnalyticsData] = useState({
    activeUsers: 0,
    activeCarts: 0
  });
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsDeviceType, setAnalyticsDeviceType] = useState('app');

  const [actionLogsTimeFrame, setActionLogsTimeFrame] = useState('5mins');
  const [actionLogsData, setActionLogsData] = useState([]);
  const [promoActionLogs, setPromoActionLogs] = useState([]);
  const [expandedPromos, setExpandedPromos] = useState({});
  const [actionLogsLoading, setActionLogsLoading] = useState(false);
  const [actionLogsDeviceType, setActionLogsDeviceType] = useState('app');

  const [chartDateFilter, setChartDateFilter] = useState('today');
  const [chartCustomDate, setChartCustomDate] = useState('');
  const [chartData, setChartData] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartDeviceType, setChartDeviceType] = useState('app');

  const [userStats, setUserStats] = useState({
    totalUsers: 0,
    appInstallBannerClicks: 0,
    joinedToday: 0,
    joinedTodayRegistered: 0,
    joinedTodayGuest: 0,
    last7Days: 0,
    last30Days: 0,
  });
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState(null);

  useEffect(() => {
    fetchUserStats();
  }, []);

  useEffect(() => {
    fetchUserAnalytics();
  }, [analyticsTimeFrame, analyticsDeviceType]);

  useEffect(() => {
    fetchActionLogsCount();
  }, [actionLogsTimeFrame, actionLogsDeviceType]);

  useEffect(() => {
    if (chartDateFilter === 'today' || chartDateFilter === 'yesterday') {
      fetchHourlySummary();
    }

    if (chartDateFilter === 'custom_date' && chartCustomDate) {
      fetchHourlySummary();
    }
  }, [chartDateFilter, chartCustomDate, chartDeviceType]);

  const fetchUserAnalytics = async () => {
    try {
      setAnalyticsLoading(true);
      const response = await analyticsAPI.getUserAnalytics(analyticsTimeFrame, analyticsDeviceType);

      if (response.success && response.data) {
        setAnalyticsData({
          activeUsers: response.data.activeUsers || 0,
          activeCarts: response.data.activeCarts || 0
        });
      }
    } catch (error) {
      console.error('Error fetching user analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const fetchUserStats = async () => {
    try {
      setStatsLoading(true);
      setStatsError(null);
      const response = await userAPI.getTotalUserStats();

      if (response.success && response.data) {
        setUserStats({
          totalUsers: response.data.total || 0,
          appInstallBannerClicks: response.data.appInstallBannerClicks || 0,
          joinedToday: response.data.today || 0,
          joinedTodayRegistered: response.data.todayRegistered || 0,
          joinedTodayGuest: response.data.todayGuest || 0,
          last7Days: response.data.last7Days || 0,
          last30Days: response.data.last30Days || 0,
        });
      } else {
        setStatsError(response.msg || 'Failed to fetch user stats');
      }
    } catch (error) {
      console.error('Error fetching user stats:', error);
      setStatsError('Error fetching user stats');
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchActionLogsCount = async () => {
    try {
      setActionLogsLoading(true);
      const response = await analyticsAPI.getUserActionLogsCount(actionLogsTimeFrame, actionLogsDeviceType);

      if (response.success && response.data) {
        // Support both legacy array and new { total, byPromo } shapes
        if (Array.isArray(response.data)) {
          setActionLogsData(response.data);
          setPromoActionLogs([]);
        } else {
          setActionLogsData(response.data.total || []);
          setPromoActionLogs(response.data.byPromo || []);
        }
        setExpandedPromos({});
      }
    } catch (error) {
      console.error('Error fetching action logs count:', error);
    } finally {
      setActionLogsLoading(false);
    }
  };

  const formatActionType = (type) => {
    return String(type || '')
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const togglePromo = (promo) => {
    setExpandedPromos(prev => ({ ...prev, [promo]: !prev[promo] }));
  };

  const fetchHourlySummary = async () => {
    try {
      setChartLoading(true);
      const response = await analyticsAPI.getHourlySummary(chartDateFilter, chartCustomDate, chartDeviceType);
      if (response.success && response.data) {
        setChartData(response.data.chartData || []);
      }
    } catch (error) {
      console.error('Error fetching hourly summary:', error);
    } finally {
      setChartLoading(false);
    }
  };

  const formatHour12 = (hour) => {
    if (hour === 0) return '12:00 AM';
    if (hour < 12) return `${hour}:00 AM`;
    if (hour === 12) return '12:00 PM';
    return `${hour - 12}:00 PM`;
  };

  return (
    <div className="analytics-page">
      <div className="analytics-page-header">
        <div className="analytics-page-title">
          <h1>Analytics Dashboard</h1>
          <p>Real-time insights and user activity metrics</p>
        </div>
      </div>

      {statsError ? (
        <div className="users-error-state">
          <p>{statsError}</p>
          <button type="button" onClick={fetchUserStats} className="users-retry-button">
            <RefreshCw size={16} />
            Retry
          </button>
        </div>
      ) : (
        <div className="users-stats-grid">
          <div className="users-stat-card">
            <div className="users-stat-icon-circle blue">
              <UsersIcon size={20} />
            </div>
            <div className="users-stat-content">
              <div className="users-stat-label">Total Users</div>
              <div className="users-stat-value">
                {statsLoading ? '-' : userStats.totalUsers}
              </div>
            </div>
          </div>

          <div className="users-stat-card">
            <div className="users-stat-icon-circle teal">
              <Smartphone size={20} />
            </div>
            <div className="users-stat-content">
              <div className="users-stat-label">App Install Banner Clicks</div>
              <div className="users-stat-value">
                {statsLoading ? '-' : userStats.appInstallBannerClicks}
              </div>
            </div>
          </div>

          <div className="users-stat-card">
            <div className="users-stat-icon-circle green">
              <Calendar size={20} />
            </div>
            <div className="users-stat-content">
              <div className="users-stat-label">Joined Today</div>
              <div className="users-stat-value">
                {statsLoading ? '-' : userStats.joinedToday}
              </div>
              {!statsLoading && (
                <div className="users-stat-subtext">
                  <span>Registered: {userStats.joinedTodayRegistered}</span>
                  <span>Guest: {userStats.joinedTodayGuest}</span>
                </div>
              )}
            </div>
          </div>

          <div className="users-stat-card">
            <div className="users-stat-icon-circle purple">
              <CalendarClock size={20} />
            </div>
            <div className="users-stat-content">
              <div className="users-stat-label">Last 7 Days</div>
              <div className="users-stat-value">
                {statsLoading ? '-' : userStats.last7Days}
              </div>
            </div>
          </div>

          <div className="users-stat-card">
            <div className="users-stat-icon-circle orange">
              <CalendarCheckIcon size={20} />
            </div>
            <div className="users-stat-content">
              <div className="users-stat-label">Last 30 Days</div>
              <div className="users-stat-value">
                {statsLoading ? '-' : userStats.last30Days}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Analytics Section */}
      <div className="analytics-section">
        <h2 className="analytics-section-title">
          <TrendingUp size={20} />
          User Analytics
        </h2>
        <div className="analytics-filters-container">
          <div className="analytics-time-frame-selector">
            <button
              className={`time-frame-button ${analyticsTimeFrame === '30mins' ? 'active' : ''}`}
              onClick={() => setAnalyticsTimeFrame('30mins')}
            >
              30 mins
            </button>
            <button
              className={`time-frame-button ${analyticsTimeFrame === '1hour' ? 'active' : ''}`}
              onClick={() => setAnalyticsTimeFrame('1hour')}
            >
              1 hour
            </button>
            <button
              className={`time-frame-button ${analyticsTimeFrame === '3hours' ? 'active' : ''}`}
              onClick={() => setAnalyticsTimeFrame('3hours')}
            >
              3 hours
            </button>
            <button
              className={`time-frame-button ${analyticsTimeFrame === '24hours' ? 'active' : ''}`}
              onClick={() => setAnalyticsTimeFrame('24hours')}
            >
              24 hours
            </button>
          </div>

          <div className="analytics-device-selector">
            <button
              className={`device-button ${analyticsDeviceType === 'app' ? 'active' : ''}`}
              onClick={() => setAnalyticsDeviceType('app')}
            >
              App
            </button>
            <button
              className={`device-button ${analyticsDeviceType === 'web' ? 'active' : ''}`}
              onClick={() => setAnalyticsDeviceType('web')}
            >
              Web
            </button>
          </div>
        </div>
        {analyticsLoading ? (
          <div className="analytics-loading-state">
            <div className="analytics-loading-spinner"></div>
            <p>Loading analytics...</p>
          </div>
        ) : (
          <div className="analytics-metrics-grid">
            <div className="analytics-metric-card">
              <div className="analytics-metric-value">
                {analyticsData.activeUsers}
              </div>
              <div className="analytics-metric-label">Active Users</div>
            </div>
            <div className="analytics-metric-card">
              <div className="analytics-metric-value">
                {analyticsData.activeCarts}
              </div>
              <div className="analytics-metric-label">Active Carts</div>
            </div>
          </div>
        )}
      </div>

      <div className="analytics-section">
        <h2 className="analytics-section-title">
          <BarChart3 size={20} />
          Hourly Activity Trends
        </h2>

        <div className="analytics-filters-container">
          <div className="analytics-time-frame-selector">
            <button
              className={`time-frame-button ${chartDateFilter === 'today' ? 'active' : ''}`}
              onClick={() => setChartDateFilter('today')}
            >
              Today
            </button>
            <button
              className={`time-frame-button ${chartDateFilter === 'yesterday' ? 'active' : ''}`}
              onClick={() => setChartDateFilter('yesterday')}
            >
              Yesterday
            </button>
            <button
              className={`time-frame-button ${chartDateFilter === 'custom_date' ? 'active' : ''}`}
              onClick={() => setChartDateFilter('custom_date')}
            >
              Custom Date
            </button>
          </div>

          <div className="analytics-device-selector">
            <button
              className={`device-button ${chartDeviceType === 'app' ? 'active' : ''}`}
              onClick={() => setChartDeviceType('app')}
            >
              App
            </button>
            <button
              className={`device-button ${chartDeviceType === 'web' ? 'active' : ''}`}
              onClick={() => setChartDeviceType('web')}
            >
              Web
            </button>
          </div>
        </div>

        {chartDateFilter === 'custom_date' && (
          <div className="custom-date-picker">
            <div className="date-input-group">
              <label>Select Date</label>
              <input
                type="date"
                value={chartCustomDate}
                onChange={(e) => setChartCustomDate(e.target.value)}
                className="date-input"
                max={new Date().toISOString().split('T')[0]}
              />
            </div>
          </div>
        )}

        {chartLoading ? (
          <div className="analytics-loading-state">
            <div className="analytics-loading-spinner"></div>
            <p>Loading hourly data...</p>
          </div>
        ) : chartData.length === 0 ? (
          <div className="analytics-empty-state">
            <p>No hourly data available</p>
          </div>
        ) : (
          <div className="hourly-chart-container">
            <div className="hourly-chart-scroll-wrapper">
              <div className="hourly-chart-inner">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={chartData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      dataKey="hour"
                      interval={0}
                      label={{ value: 'Hour of Day', position: 'insideBottom', offset: -10 }}
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                      tickFormatter={(hour) => `${hour}`}
                    />
                    <YAxis
                      allowDecimals={false}
                      tickCount={6}
                      domain={[0, 'dataMax']}
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                      label={{ value: 'Count', angle: -90, position: 'insideLeft' }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '12px'
                      }}
                      labelFormatter={(hour) => `Hour: ${hour}:00 (${formatHour12(hour)})`}
                    />
                    <Legend
                      wrapperStyle={{ paddingTop: '20px' }}
                      iconType="line"
                    />
                    <Line
                      type="monotone"
                      dataKey="active_users"
                      stroke="#3b82f6"
                      strokeWidth={2}
                      name="Active Users"
                      dot={{ fill: '#3b82f6', r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="active_carts"
                      stroke="#ef4444"
                      strokeWidth={2}
                      name="Active Carts"
                      dot={{ fill: '#ef4444', r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* User Action Logs Count Section */}
      <div className="analytics-section">
        <h2 className="analytics-section-title">
          <Activity size={20} />
          User Action Logs
        </h2>
        <div className="analytics-filters-container">
          <div className="analytics-time-frame-selector">
            <button
              className={`time-frame-button ${actionLogsTimeFrame === '5mins' ? 'active' : ''}`}
              onClick={() => setActionLogsTimeFrame('5mins')}
            >
              5 mins
            </button>
            <button
              className={`time-frame-button ${actionLogsTimeFrame === '10mins' ? 'active' : ''}`}
              onClick={() => setActionLogsTimeFrame('10mins')}
            >
              10 mins
            </button>
            <button
              className={`time-frame-button ${actionLogsTimeFrame === '30mins' ? 'active' : ''}`}
              onClick={() => setActionLogsTimeFrame('30mins')}
            >
              30 mins
            </button>
            <button
              className={`time-frame-button ${actionLogsTimeFrame === '1hour' ? 'active' : ''}`}
              onClick={() => setActionLogsTimeFrame('1hour')}
            >
              1 hour
            </button>
          </div>

          <div className="analytics-device-selector">
            <button
              className={`device-button ${actionLogsDeviceType === 'app' ? 'active' : ''}`}
              onClick={() => setActionLogsDeviceType('app')}
            >
              App
            </button>
            <button
              className={`device-button ${actionLogsDeviceType === 'web' ? 'active' : ''}`}
              onClick={() => setActionLogsDeviceType('web')}
            >
              Web
            </button>
          </div>
        </div>
        {actionLogsLoading ? (
          <div className="analytics-loading-state">
            <div className="analytics-loading-spinner"></div>
            <p>Loading action logs...</p>
          </div>
        ) : actionLogsData.length === 0 && promoActionLogs.length === 0 ? (
          <div className="analytics-empty-state">
            <p>No user action logs found</p>
          </div>
        ) : (
          <>
            {actionLogsData.length > 0 && (
              <>
                <h3 className="analytics-subsection-title">All Actions</h3>
                <div className="action-logs-grid">
                  {actionLogsData.map((log, index) => (
                    <div key={index} className="action-log-card">
                      <div className="action-log-label">
                        {formatActionType(log._id)}
                      </div>
                      <div className="action-log-count">
                        {log.count}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {promoActionLogs.length > 0 && (
              <div className="promo-actions-section">
                <h3 className="analytics-subsection-title">
                  <Megaphone size={16} />
                  Promo / Ad Actions
                </h3>
                <div className="promo-actions-list">
                  {promoActionLogs.map((promoItem) => {
                    const isExpanded = !!expandedPromos[promoItem.promo];
                    return (
                      <div key={promoItem.promo} className="promo-action-card">
                        <button
                          type="button"
                          className="promo-action-header"
                          onClick={() => togglePromo(promoItem.promo)}
                        >
                          <span className="promo-action-header-left">
                            {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                            <span className="promo-action-name">{promoItem.promo}</span>
                          </span>
                          <span className="promo-action-total">{promoItem.totalActions} actions</span>
                        </button>
                        {isExpanded && (
                          <div className="action-logs-grid promo-action-grid">
                            {(promoItem.actions || []).map((log, index) => (
                              <div key={`${promoItem.promo}-${log._id}-${index}`} className="action-log-card">
                                <div className="action-log-label">
                                  {formatActionType(log._id)}
                                </div>
                                <div className="action-log-count">
                                  {log.count}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Analytics;